<template>
  <aside
    v-if="!isWorkspaceLoading"
    :class="[
      'side-navigation',
      'has-background-primary',
      { 'side-navigation--is-active': isSideNavigationOpen }
    ]"
  >
    <div class="u-m-b-large">
      <div v-if="isSideNavigationOpen">
        <b-icon
          v-if="isSideNavigationOpen"
          icon="arrow-left"
          class="side-navigation__menu-icon"
          @click.native="handleToggleSideNavigation(false)"
        />
        &nbsp;
      </div>
      <b-dropdown
        class="is-block u-m-t"
        :mobile-modal="false"
        @active-change="handleToggleWorkspaceDropdown"
      >
        <span slot="trigger">
          <h5 class="side-navigation__workspace-name title is-5 has-text-white">
            {{ truncateString(activeWorkspace.workspaceName, 10) }}
            <b-icon
              :class="{
                'side-navigation__workspace-name__icon': true,
                'side-navigation__workspace-name__icon--active': isWorkspaceDropdownActive,
              }"
              icon="chevron-down"
            />
          </h5>
        </span>
        <b-dropdown-item @click="handleUsersClick">
          <b-icon icon="account" size="is-small" />&nbsp;Users
        </b-dropdown-item>
        <b-dropdown-item @click="handleTeamsClick">
          <b-icon icon="account-multiple" size="is-small" />&nbsp;Teams&nbsp;
        </b-dropdown-item>
        <b-dropdown-item v-if="isLoggedInUserWorkspaceOwner" separator />
        <b-dropdown-item v-if="isLoggedInUserWorkspaceOwner" @click="handleSettingsClick">
          <b-icon icon="cog" size="is-small" />&nbsp;Workspace Settings
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <h6 class="title is-6 has-text-white">
      <router-link to="/dashboard" class="has-text-white">MY DASHBOARD</router-link>
    </h6>
    <h6 class="title is-6 has-text-white">
      <router-link to="/analytics" class="has-text-white">ANALYTICS</router-link>
    </h6>
    <h6 class="title is-6 has-text-white u-m-b-small">TEAMS</h6>
    <ul v-if="workspaceTeams" class="sublist">
      <li
        v-for="team in workspaceTeamsToArray"
        :key="team.key"
        @click="handleTeamClick(team.id)"
        @keydown.enter="handleTeamClick(team.id)"
      >
        <a class="side-navigation__teams__team-name">{{ team.teamName }}</a>
      </li>
    </ul>
    <ul v-else>
      <li class="text-placeholder-blur">...No teams created</li>
    </ul>
  </aside>
</template>

<script>
import {
  mapActions,
  mapState,
  mapGetters,
  mapMutations,
} from 'vuex';
import * as TeamActions from '@/store/actions/Team.actions';
import * as UiMutations from '@/store/mutations/Ui.mutations';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { truncateString } from '@/helpers/stringHelpers';

export default {
  name: 'SideNavigation',

  data() {
    return {
      isWorkspaceDropdownActive: false,
    };
  },

  computed: {
    ...mapState({
      isSideNavigationOpen: state => state.ui.isSideNavigationOpen,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    ...mapGetters([
      'workspaceTeams',
      'activeWorkspace',
      'isLoggedInUserWorkspaceOwner',
      'loggedInUser',
    ]),
    workspaceTeamsToArray() {
      if (!this.workspaceTeams) {
        return [];
      }
      return Object.keys(this.workspaceTeams).map(key => this.workspaceTeams[key]);
    },
    isWorkspaceLoading() {
      return isObjectEmpty(this.activeWorkspace);
    },
  },

  created() {
    this.fetchPublicWorkspaceTeams({ workspaceId: this.activeWorkspaceId });
    this.fetchUserTeams({ userId: this.loggedInUser.id, workspaceId: this.activeWorkspace.id });
  },

  methods: {
    ...mapActions({
      fetchPublicWorkspaceTeams: TeamActions.FETCH_PUBLIC_WORKSPACE_TEAMS,
      fetchUserTeams: TeamActions.FETCH_USER_TEAMS,
    }),
    ...mapMutations({
      setSideNavigationState: UiMutations.SET_SIDE_NAVIGATION_STATE,
    }),
    handleTeamClick(teamId) {
      this.handleToggleSideNavigation(false);
      this.$router.push(`/teams/${teamId}`);
    },
    handleToggleSideNavigation(isSideNavigationOpen) {
      this.setSideNavigationState({ isSideNavigationOpen });
    },
    handleToggleWorkspaceDropdown(isDropdownActive) {
      this.isWorkspaceDropdownActive = isDropdownActive;
    },
    handleUsersClick() {
      this.$router.push('/users');
    },
    handleTeamsClick() {
      this.$router.push('/teams');
    },
    handleSettingsClick() {
      this.$router.push('/workspace/settings');
    },
    truncateString,
  },
};
</script>

<style lang="scss" scoped>

.side-navigation {
  top: 0;
  bottom: 0;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  position: fixed;
  width: $side-navigation-width;
  z-index: 20;
  padding: $global-whitespace-medium;
  color: $white;
  transition: transform 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);

  @media screen and (max-width: $breakpoint-to-hide-side-navigation) {
    transform: translateX(-$side-navigation-width);
    z-index: 99999999;

    &--is-active {
      transform: translateX(0);
    }
  }

  ul {
    li {
      padding: $global-whitespace-xsmall $global-whitespace-small;
      cursor: pointer;

      &:hover:not(.placeholder) {
        background-color: rgba($white, 0.1);
      }
    }
  }
}

.side-navigation__teams__team-name {
  color: $white;

  &:hover {
    color: $white;
  }
}

.side-navigation__menu-icon {
  margin-right: 1rem;
  @media screen and (min-width: $breakpoint-to-hide-side-navigation + 1) {
    display: none;
  }
}

.text-placeholder-blur {
  color: transparent;
  text-shadow: 0 0 10px $white;
}

.logo {
  img {
    height: 20px;
  }
}

.side-navigation__workspace-name {
  cursor: pointer;
}

.side-navigation__workspace-name__icon {
  vertical-align: text-bottom;
  transition: transform 0.2s cubic-bezier(0.075, 0.82, 0.165, 1)
}

.side-navigation__workspace-name__icon--active {
  transform: rotate(-180deg);
}
</style>
